import { Label } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { InfoHint } from "@/components/ui/info-hint";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { useVisitorTagField } from "@/lib/useVisitorTagField";
import { cn } from "@/lib/utils";

/**
 * VisitorTagField — Tag-Auswahl der Besuchermaske, identisch zum Web.
 *
 * Bezeichnung, Beschreibung, Einfach-/Mehrfachauswahl und Pflicht kommen aus
 * den Visitor-Modul-Settings des Standorts. Der Wert ist immer ein Array von
 * Tag-Labels, auch bei Einfachauswahl — so bleibt das Datenmodell über beide
 * Modi gleich.
 *
 * Ist eine Feld-Beschreibung hinterlegt, erscheint sie als Info-Icon neben der
 * Bezeichnung; die Beschreibung eines einzelnen Tags zeigt das Dropdown an der
 * jeweiligen Option.
 *
 * Sind an einem Besucher mehrere Tags gesetzt, obwohl inzwischen Einfachauswahl
 * konfiguriert ist, bleibt die Mehrfach-Auswahl sichtbar — sonst wären die
 * übrigen Tags unsichtbar.
 *
 * `error` umrandet das Feld rot — für Masken, die eine fehlende Pflichtauswahl
 * beim Absenden anzeigen.
 *
 * Ohne verfügbare Optionen rendert die Komponente nichts.
 */
export function VisitorTagField({
  value = [],
  options = [],
  onChange,
  locationId,
  disabled = false,
  error = false,
  className,
}) {
  const { label, description, multi, required } = useVisitorTagField(locationId);
  if (!options.length) return null;

  const asMulti = multi || value.length > 1;

  return (
    <div className={className}>
      <Label className="flex items-center gap-[0.4rem]">
        {required ? `${label} *` : label}
        <InfoHint text={description} />
      </Label>
      {asMulti ? (
        <MultiCombobox
          value={value}
          options={options}
          onChange={onChange}
          disabled={disabled}
          className={cn(error && "border-danger")}
        />
      ) : (
        <Combobox
          value={value[0] ?? null}
          options={options}
          onChange={(v) => onChange?.(v ? [v] : [])}
          disabled={disabled}
          clearable
          className={cn(error && "border-danger")}
        />
      )}
    </div>
  );
}
